import { useQuery } from "@tanstack/react-query";
import { format, isSameDay, parseISO, startOfDay } from "date-fns";
import { max, minBy } from "lodash-es";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useMemo } from "react";

import type { Period } from "@/components/Analytics/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { Share01Icon } from "@/components/Icons/Share01Icon";
import { Overlay } from "@/components/Overlay/Overlay";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PeriodPicker } from "@/components/PeriodPicker/PeriodPicker";
import { filterProjectsByFilters } from "@/components/ProjectFilter/filterProjectsByFilters";
import type { PortfolioProjectFilterValues } from "@/components/ProjectFilter/PortfolioProjectFilterFields";
import { ProjectList } from "@/components/ProjectSwitcher/ProjectList";
import { RadioCard } from "@/components/Radio/RadioCard";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { Skeleton } from "@/design-system/Skeleton";
import { MAX_ANALYTICS_DATE, MIN_ANALYTICS_DATE } from "@/helpers/analytics";
import { parseQueryParamToDate, parseQueryParamToList } from "@/helpers/query-params";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useKey } from "@/hooks/useKey";
import { AnalyticsOverviewSections } from "@/modules/analytics/components/AnalyticsOverviewSections";
import { usePortfolioAnalyticsQueries } from "@/queries/portfolio-analytics/queryOptions";
import { portfolioMutations } from "@/queries/portfolio/mutations";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";
import { useTranslation } from "@/translations";
import { RuntimeFloatingPortal, useRuntimeFloatingPanel } from "@/ui-adapters/RuntimeFloatingPanel";

import { PortfolioProjectFilterDrawer } from "../../components/PortfolioProjectFilterDrawer";
import { usePortfolioOverviewQueryParams } from "./usePortfolioOverviewQueryParams";

export function PortfolioOverviewPage(): React.ReactNode {
  const clockDate = useCurrentDate();
  const [queryParams, setQueryParams] = usePortfolioOverviewQueryParams();
  const [isFilterOpened, filtersOpenHandler] = useBool();
  const [isConfirmExportModalOpened, confirmExportModalHandler] = useBool();
  const [isProjectSwitcherVisible, projectSwitcherVisibilityHandlers] = useBool();
  const { referenceRef, panelProps } = useRuntimeFloatingPanel({
    open: isProjectSwitcherVisible,
    // Align the panel's inner search field over its trigger.
    mainAxis: -56,
    crossAxis: -16,
    viewportPadding: 16,
  });
  useKey("Escape", projectSwitcherVisibilityHandlers.setFalse, isProjectSwitcherVisible);

  const sessionUser = useSessionUser();
  const { t, i18n } = useTranslation();
  const postHog = usePostHog();
  const showFlashToast = useFlashToast();

  const portfolioAnalyticsQueries = usePortfolioAnalyticsQueries();
  const { data: fahAnalytics } = useQuery(portfolioAnalyticsQueries.fah());
  const exportProjectAnalyticsMutation = portfolioMutations.useExportProjectAnalytics();

  const currFilter = useMemo(() => {
    return {
      city: parseQueryParamToList(queryParams.city),
      owner: parseQueryParamToList(queryParams.owner),
      maintenance: parseQueryParamToList(queryParams.maintenance),
      startDate: parseQueryParamToDate(queryParams.startDate),
    };
  }, [queryParams.city, queryParams.owner, queryParams.maintenance, queryParams.startDate]);
  const selfAccountQueries = useSelfAccountQueries();
  const { data: linkedProjects = [] } = useQuery(selfAccountQueries.listLinkedProjects());
  const filteredProjects = filterProjectsByFilters(linkedProjects, currFilter);

  // Update folded project filters
  const handleUpdateProjectFilter = (newFilter: PortfolioProjectFilterValues) => {
    setQueryParams({
      ...queryParams,
      city: newFilter.city?.join(",") || undefined,
      owner: newFilter.owner?.join(",") || undefined,
      maintenance: newFilter.maintenance?.join(",") || undefined,
    });
  };

  // Update date period filter
  const handleUpdateDatePeriod = (from: Date | undefined) => {
    setQueryParams({
      ...queryParams,
      startDate: from ? format(from, "yyyy-MM-dd") : undefined,
    });
  };

  const exportAnalytics = () => {
    exportProjectAnalyticsMutation.mutate(
      {
        data: {
          projectIds: filteredProjects.map((project) => project.id),
        },
        // oxlint-disable-next-line react/purity -- This date is read only when the user clicks.
        fileName: `portfolio_analytics_${format(new Date(), "yyyy-MM-dd")}_last_90_days.xlsx`,
      },
      {
        onSettled: () => {
          confirmExportModalHandler.setFalse();
        },
      },
    );
  };

  const handleSharePage = () => {
    const urlToBeShared = window.location.href;

    void navigator.clipboard
      .writeText(urlToBeShared)
      .then(() => {
        postHog.capture("clicked_share_portfolio_page", {
          url: urlToBeShared,
        });
      })
      .then(() => {
        showFlashToast({
          variant: "success",
          message: t("page.portfolio-home.share.notifications.success"),
        });
      })
      .catch(() => {
        showFlashToast({
          variant: "alert",
          message: t("page.portfolio-home.share.notifications.failed"),
        });
      });
  };

  const handleExportAnalytics = () => {
    postHog.capture("clicked_export_portfolio_analytics", {
      projectIds: filteredProjects.map((project) => project.id),
    });

    confirmExportModalHandler.setTrue();
  };

  const minProjectCreationDate = minBy(filteredProjects.map((x) => parseISO(x.createdAt)) ?? [], (x) => x.valueOf());
  const listFilteredAddressBasedProjectId = filteredProjects
    .filter((project) => project.projectType === "addressBased")
    .map((project) => project.id);
  const listFilteredProjectId = filteredProjects.map((project) => project.id);

  // Create period object from currFilter
  const period: Period = currFilter.startDate
    ? { period: "quarter", startDate: currFilter.startDate }
    : { period: "last30Days" };

  const amountActiveFilters = Object.entries(queryParams).filter(([key, value]) => {
    // Only count filters that are shown in the filter drawer
    const shouldAccountToCount = (
      ["owner", "city", "maintenance"] satisfies (keyof PortfolioProjectFilterValues)[]
    ).includes(key as any);

    return (
      shouldAccountToCount &&
      ((Array.isArray(value) && value.length > 0) || (typeof value === "string" && value.trim() !== ""))
    );
  }).length;

  const dateNow = startOfDay(clockDate);
  let greeting = t("common.greeting.morning");
  if (dateNow.getHours() >= 12 && dateNow.getHours() < 18) {
    greeting = t("common.greeting.afternoon");
  } else if (dateNow.getHours() >= 18) {
    greeting = t("common.greeting.evening");
  }

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.portfolio.title")}
        title={`${greeting}, ${sessionUser.firstName}`}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.portfolio.title"),
              },
              { name: t("page.portfolio-home.title") },
            ]}
          />
        }
        actions={
          <>
            <AskNobiButton
              defaultFilters={{}}
              context={{
                scope: "portfolio",
                entity: "portfolio",
                page: "detail",
                filters: {
                  city: currFilter.city,
                  owner: currFilter.owner,
                  maintenance: currFilter.maintenance,
                  startDate: currFilter.startDate ? format(currFilter.startDate, "yyyy-MM-dd") : undefined,
                },
              }}
              suggestions={[t("component.ask-nobi-chat-drawer.portfolio.suggestion.1")]}
            />
            <Button onClick={handleSharePage} icon={<Share01Icon />} variant="soft">
              {t("common.action.share")}
            </Button>
            <Button onClick={handleExportAnalytics} icon={<Download01Icon />} data-testid="export-analytics-btn">
              {t("page.portfolio-home.btn.export")}
            </Button>
          </>
        }
        header={
          <Inline gap="md" align="center" justify="between">
            <Inline gap="sm" align="center">
              <div className="max-w-measure-compact">
                <SearchInput
                  placeholder={t("component.project-switcher.search.placeholder")}
                  name="portfolio-overview-project-search"
                  aria-label={t("component.project-switcher.search.placeholder")}
                  onFocus={projectSwitcherVisibilityHandlers.setTrue}
                  ref={referenceRef}
                />
              </div>
              {isProjectSwitcherVisible && (
                <RuntimeFloatingPortal>
                  <nav
                    {...panelProps}
                    className="z-overlay w-measure-compact overflow-hidden rounded-lg bg-background-default shadow-raised"
                  >
                    <ProjectList
                      projects={linkedProjects}
                      maxProjects={3}
                      showCurrentProject
                      onClose={projectSwitcherVisibilityHandlers.setFalse}
                      autoFocusSearchInput
                    />
                  </nav>
                  <Overlay onClick={projectSwitcherVisibilityHandlers.setFalse} />
                </RuntimeFloatingPortal>
              )}
              <Button
                data-testid="portfolio-filter-btn"
                icon={<FilterFunnel01Icon />}
                variant="soft"
                onClick={filtersOpenHandler.toggle}
                aria-pressed={isFilterOpened}
              >
                <Inline gap="sm" align="center" wrap={false}>
                  {t("common.action.filter")}
                  {amountActiveFilters > 0 && <NumericBadge count={amountActiveFilters} variant="filled" />}
                </Inline>
              </Button>
              <PeriodPicker
                type="quarter"
                value={currFilter.startDate ? startOfDay(currFilter.startDate) : dateNow}
                onChange={(startDate) => handleUpdateDatePeriod(startDate)}
                min={max([MIN_ANALYTICS_DATE, minProjectCreationDate ?? MIN_ANALYTICS_DATE])}
                max={MAX_ANALYTICS_DATE}
                renderSelected={(date) => {
                  if (date.getTime() === dateNow.getTime()) {
                    return t("page.portfolio.overview.filters.month.latest");
                  }

                  return formatDate(i18n, "quarter", date);
                }}
                renderFooterComponent={
                  fahAnalytics
                    ? (onClose) => (
                        <Box padding={{ y: "sm" }}>
                          <RadioCard
                            name="last30Days"
                            value={dateNow.toDateString()}
                            onChange={() => {
                              handleUpdateDatePeriod(undefined);
                              onClose();
                            }}
                            checked={currFilter.startDate ? isSameDay(currFilter.startDate, dateNow) : true}
                            label={t("page.portfolio.overview.filters.month.latest")}
                            description={`${formatDate(i18n, "dateMonthYearShort", fahAnalytics?.startDate)} - ${formatDate(i18n, "dateMonthYearShort", fahAnalytics.endDate)}`}
                          />
                        </Box>
                      )
                    : undefined
                }
              />
            </Inline>
            {fahAnalytics?.endDate ? (
              <p className="text-caption whitespace-nowrap text-text-secondary">
                {t("page.portfolio-home.label.last-updated-at", {
                  datetime: formatDate(i18n, "datetime", fahAnalytics.endDate),
                })}
              </p>
            ) : (
              <div className="max-w-measure-compact">
                <Skeleton height="4" />
              </div>
            )}
          </Inline>
        }
      >
        <AnalyticsOverviewSections
          projectIds={listFilteredProjectId}
          feelingAtHomeProjectIds={listFilteredAddressBasedProjectId}
          period={period}
        />
      </DocumentPage>

      <PortfolioProjectFilterDrawer
        isOpened={isFilterOpened}
        onOpenChange={filtersOpenHandler.set}
        onUpdate={handleUpdateProjectFilter}
        projects={linkedProjects}
        selectedFilter={{
          city: currFilter.city || [],
          maintenance: currFilter.maintenance || [],
          owner: currFilter.owner || [],
        }}
      />

      <ConfirmModal
        tone="primary"
        isOpened={isConfirmExportModalOpened}
        onOpenChange={confirmExportModalHandler.set}
        isLoading={exportProjectAnalyticsMutation.isPending}
        title={t("page.portfolio.overview.export-analytics-modal.title")}
        description={t("page.portfolio.overview.export-analytics-modal.description", {
          projectCount: filteredProjects.length,
        })}
        data-testid="portfolio-confirm-export-modal"
        onReject={() => confirmExportModalHandler.setFalse()}
        onResolve={exportAnalytics}
        resolveBtnProps={{
          text: t("common.action.export"),
          "data-testid": "confirm-export-btn",
        }}
      />
    </>
  );
}
